import React, { useEffect, useState } from 'react';
import './styles.scss';

interface BountySummary {
  holdId: string;
  holdName?: string;
  amount: number;
}

interface RevealDetail {
  fromName: string;
  type: 'citizenship' | 'passport';
  issuerId: string;
  issuerName: string;
  holderName: string;
  issuedAt: number;
  genuineVisible?: boolean;
  bounties?: BountySummary[];
}

type View = 'hidden' | 'prompt' | 'viewing';

const send = (payload: Record<string, unknown>): void => {
  try {
    (window as any).skyrimPlatform.sendMessage(JSON.stringify(payload));
  } catch (e) {
    // Running outside the game (e.g. Storybook) - log instead.
    // eslint-disable-next-line no-console
    console.log('papersShow sendMessage', payload);
  }
};

function formatDate(ts: number): string {
  const d = new Date(ts);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}

function cardClass(type: RevealDetail['type'], issuerId: string): string {
  if (type === 'passport' && issuerId === 'thalmor') return 'papers-view-card papers-view-card--thalmor';
  if (type === 'passport' && issuerId === 'empire') return 'papers-view-card papers-view-card--empire';
  return 'papers-view-card papers-view-card--citizenship';
}

// Single component, two independent CustomEvent-driven states (prompt then
// viewing) rather than two mount roots - same "one component, a view state
// machine" pattern as mailbox/index.tsx.
const PapersShow: React.FC = () => {
  const [view, setView] = useState<View>('hidden');
  const [promptFrom, setPromptFrom] = useState('');
  const [promptLabel, setPromptLabel] = useState('');
  const [reveal, setReveal] = useState<RevealDetail | null>(null);

  useEffect(() => {
    const showConsent = (e: Event) => {
      const detail = (e as CustomEvent).detail as { fromName?: string; label?: string } | null;
      setPromptFrom(detail?.fromName ?? "Quelqu'un");
      setPromptLabel(detail?.label ?? 'ses papiers');
      setView('prompt');
    };
    const hideConsent = () => setView((v) => (v === 'prompt' ? 'hidden' : v));
    const showView = (e: Event) => {
      const detail = (e as CustomEvent).detail as RevealDetail | null;
      if (detail) setReveal(detail);
      setView('viewing');
    };
    const hideView = () => setView((v) => (v === 'viewing' ? 'hidden' : v));

    window.addEventListener('skymp5-client:showPapersConsent', showConsent);
    window.addEventListener('skymp5-client:hidePapersConsent', hideConsent);
    window.addEventListener('skymp5-client:showPapersView', showView);
    window.addEventListener('skymp5-client:hidePapersView', hideView);
    return () => {
      window.removeEventListener('skymp5-client:showPapersConsent', showConsent);
      window.removeEventListener('skymp5-client:hidePapersConsent', hideConsent);
      window.removeEventListener('skymp5-client:showPapersView', showView);
      window.removeEventListener('skymp5-client:hidePapersView', hideView);
    };
  }, []);

  if (view === 'hidden') return null;

  if (view === 'prompt') {
    const respond = (accepted: boolean) => {
      setView('hidden');
      send({ action: 'papersConsent', accepted });
    };
    return (
      <div className="papers-view-overlay">
        <div className="papers-view-prompt skyrim-panel">
          <div className="papers-view-prompt-title">Demande</div>
          <p className="papers-view-prompt-text">
            <strong>{promptFrom}</strong> veut te montrer : {promptLabel}. Accepter ?
          </p>
          <div className="papers-view-prompt-actions">
            <button type="button" className="papers-view-btn papers-view-btn--cancel" onClick={() => respond(false)}>
              Refuser
            </button>
            <button type="button" className="papers-view-btn papers-view-btn--confirm" onClick={() => respond(true)}>
              Autoriser
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!reveal) return null;

  const label = reveal.type === 'citizenship' ? `Certificat de citoyenneté - ${reveal.issuerName}` : `Passeport - ${reveal.issuerName}`;
  const close = () => {
    setView('hidden');
    send({ action: 'closeView' });
  };

  return (
    <div className="papers-view-overlay">
      <div className={`${cardClass(reveal.type, reveal.issuerId)} skyrim-panel`}>
        <div className="papers-view-card-from">Présenté par {reveal.fromName}</div>
        <div className="papers-view-card-label">{label}</div>
        <div className="papers-view-card-holder">{reveal.holderName}</div>
        <div className="papers-view-card-date">Délivré le {formatDate(reveal.issuedAt)}</div>
        {reveal.genuineVisible !== undefined && (
          <div className={`papers-view-card-status${reveal.genuineVisible ? '' : ' papers-view-card-status--fake'}`}>
            {reveal.genuineVisible ? 'AUTHENTIQUE' : 'FAUX'}
          </div>
        )}
        {reveal.bounties && reveal.bounties.length > 0 && (
          <div className="papers-view-card-bounties">
            {reveal.bounties.map((b) => (
              <div key={b.holdId} className="papers-view-card-bounty">
                Recherché à {b.holdName ?? b.holdId} : {b.amount} Or
              </div>
            ))}
          </div>
        )}
        <button type="button" className="papers-view-btn papers-view-btn--cancel papers-view-card-close" onClick={close}>
          Fermer
        </button>
      </div>
    </div>
  );
};

export default PapersShow;
